// Template 2: Modern Gradient
// Features: Gradient header, card-based layout, social icons

import { 
  Phone, 
  MessageCircle, 
  Mail, 
  Share2, 
  Download,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  MapPin,
  ExternalLink
} from 'lucide-react'

export function ModernGradientTemplate({ vcard, onSaveContact, onShare }) {
  return (
    <div className="modern-gradient-template">
      {/* Gradient Header */}
      <div className="mg-header">
        <div className="mg-header-content">
          <div 
            className="mg-avatar"
            style={{ 
              backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none'
            }}
          >
            {!vcard.profileImage && (
              <span>{vcard.name?.[0] || 'U'}</span>
            )}
          </div>
          <h1 className="mg-name">{vcard.name || 'Your Name'}</h1>
          <p className="mg-title">{vcard.title || 'Your Title'}</p>
          {vcard.company && <p className="mg-company">{vcard.company}</p>}
        </div>
        <div className="mg-wave">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path fill="#ffffff" d="M0,64L60,58.7C120,53,240,43,360,48C480,53,600,75,720,80C840,85,960,75,1080,64C1200,53,1320,43,1380,37.3L1440,32L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      {/* Card Content */}
      <div className="mg-content">
        {/* Quick Actions */}
        <div className="mg-quick-actions">
          {vcard.phone && (
            <a href={`tel:${vcard.phone}`} className="mg-action-btn">
              <Phone size={22} />
              <span>Call</span>
            </a>
          )}
          {vcard.whatsapp && (
            <a 
              href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mg-action-btn whatsapp"
            >
              <MessageCircle size={22} />
              <span>WhatsApp</span>
            </a>
          )}
          {vcard.email && (
            <a href={`mailto:${vcard.email}`} className="mg-action-btn">
              <Mail size={22} />
              <span>Email</span>
            </a>
          )}
        </div>

        {/* About */}
        {vcard.about && (
          <div className="mg-card">
            <h3 className="mg-card-title">About</h3>
            <p className="mg-about">{vcard.about}</p>
          </div>
        )}

        {/* Services */}
        {vcard.services && vcard.services.length > 0 && (
          <div className="mg-card">
            <h3 className="mg-card-title">Services</h3>
            <div className="mg-services">
              {vcard.services.map((service, i) => (
                <span key={i} className="mg-service-tag">{service}</span>
              ))}
            </div>
          </div>
        )}

        {/* Gallery */}
        {vcard.gallery && vcard.gallery.length > 0 && (
          <div className="mg-card">
            <h3 className="mg-card-title">Gallery</h3>
            <div className="mg-gallery">
              {vcard.gallery.map((image, i) => (
                <div key={i} className="mg-gallery-item">
                  <img src={image} alt={`Gallery ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social Links */}
        {vcard.social && Object.values(vcard.social).some(v => v) && (
          <div className="mg-card">
            <h3 className="mg-card-title">Connect</h3>
            <div className="mg-social-list">
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link linkedin"
                >
                  <Linkedin size={20} />
                  <span>LinkedIn</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link instagram"
                >
                  <Instagram size={20} />
                  <span>Instagram</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link facebook"
                >
                  <Facebook size={20} />
                  <span>Facebook</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {vcard.social.youtube && (
                <a 
                  href={vcard.social.youtube.startsWith('http') ? vcard.social.youtube : `https://youtube.com/${vcard.social.youtube}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link youtube"
                >
                  <Youtube size={20} />
                  <span>YouTube</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link twitter"
                >
                  <Twitter size={20} />
                  <span>Twitter</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mg-social-link website"
                >
                  <Globe size={20} />
                  <span>Website</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Location */}
        {vcard.address && (
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mg-location"
          >
            <MapPin size={20} />
            <span>{vcard.address}</span>
            <ExternalLink size={16} />
          </a>
        )}

        {/* Main Actions */}
        <div className="mg-main-actions">
          <button onClick={onSaveContact} className="mg-btn primary">
            <Download size={20} />
            Save Contact
          </button>
          <button onClick={onShare} className="mg-btn secondary">
            <Share2 size={20} />
            Share
          </button>
        </div>

        {/* Footer */}
        <footer className="mg-footer">
          <p>Powered by <a href="/">Digital vCard</a></p>
        </footer>
      </div>
    </div>
  )
}

export default ModernGradientTemplate
